(function (root) {
  'use strict';

  function exactVariant(variants, selected) {
    return variants.find(function (variant) {
      return variant.options.length === selected.length && variant.options.every(function (value, index) {
        return value === selected[index];
      });
    }) || null;
  }

  function money(cents, currency) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' }).format(cents / 100);
  }

  async function addVariant(variant, env) {
    var drawer = env.drawer;
    var payload = { items: [{ id: variant.id, quantity: 1 }] };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map(function (section) { return section.id; });
      payload.sections_url = env.path;
    }
    var response;
    try {
      response = await env.fetch(env.root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      throw new Error('We could not confirm the addition. Please check your cart before trying again.');
    }
    var data;
    try { data = await response.json(); } catch (error) {
      throw new Error('We could not confirm the addition. Please check your cart before trying again.');
    }
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(typeof data.description === 'string' ? data.description : 'This selection could not be added. Please try another option.');
    }
    if (!Array.isArray(data.items) || !data.items.some(function (item) { return String(item.id) === String(variant.id) || String(item.variant_id) === String(variant.id); })) {
      throw new Error('We could not confirm the addition. Please check your cart before trying again.');
    }
    // Addition succeeded. A drawer refresh failure must never invite a duplicate retry.
    var refreshed = false;
    if (drawer && typeof drawer.renderContents === 'function' && data.sections && Object.values(data.sections).every(Boolean)) {
      try { drawer.renderContents(data); refreshed = true; } catch (error) { /* The cart link remains available. */ }
    }
    if (typeof env.monsterRefresh === 'function') {
      try { env.monsterRefresh(); } catch (error) { /* Addition remains successful. */ }
    }
    try {
      var cartResponse = await env.fetch(env.root + 'cart.js', { headers: { 'Accept': 'application/json' } });
      if (cartResponse.ok) env.onCart(await cartResponse.json());
    } catch (error) { /* Do not repeat a successful addition for a failed count refresh. */ }
    return { data: data, refreshed: refreshed };
  }

  function createController(card, env) {
    var variants = JSON.parse(card.getAttribute('data-variants'));
    var selected = JSON.parse(card.getAttribute('data-selected'));
    var button = card.querySelector('.cross-sell-add');
    var status = card.querySelector('.cs-status');
    var controls = Array.from(card.querySelectorAll('[data-cs-option]'));
    var busy = false;
    var touched = false;

    function render() {
      var variant = exactVariant(variants, selected);
      controls.forEach(function (control) {
        var index = Number(control.getAttribute('data-cs-option'));
        if (control.tagName === 'SELECT') control.value = selected[index];
        else {
          var active = control.getAttribute('data-value') === selected[index];
          control.classList.toggle('active', active);
          control.setAttribute('aria-pressed', String(active));
        }
        control.disabled = busy;
      });
      card.querySelectorAll('[data-cs-selected]').forEach(function (label) {
        label.textContent = selected[Number(label.getAttribute('data-cs-selected'))];
      });
      button.setAttribute('data-variant-id', variant && variant.available ? String(variant.id) : '');
      button.disabled = busy || !variant || !variant.available;
      button.textContent = busy ? 'Adding...' : !variant ? 'Unavailable' : !variant.available ? 'Sold out' : '+ Add to Cart';
      button.setAttribute('aria-busy', String(busy));
      var price = card.querySelector('.cs-price');
      if (price) price.textContent = variant ? money(variant.price, env.currency) : 'Unavailable in this combination';
      var image = card.querySelector('.cs-img');
      if (image && variant && variant.image) image.src = variant.image;
      if (image) image.alt = card.getAttribute('data-title') + ' - ' + selected.join(' / ');
      return variant;
    }

    function choose(index, value) {
      if (busy) return;
      touched = true;
      selected[index] = value;
      status.textContent = '';
      render();
    }

    controls.forEach(function (control) {
      control.addEventListener(control.tagName === 'SELECT' ? 'change' : 'click', function () {
        choose(Number(control.getAttribute('data-cs-option')), control.tagName === 'SELECT' ? control.value : control.getAttribute('data-value'));
      });
    });

    async function add() {
      if (busy) return;
      var variant = exactVariant(variants, selected);
      if (!variant || !variant.available) return;
      busy = true;
      status.textContent = '';
      render();
      try {
        var result = await addVariant(variant, env);
        status.textContent = 'Added: ' + selected.join(' / ') + (result.refreshed ? '.' : '. View your cart to check your items.');
      } catch (error) {
        status.textContent = error.message || 'Please check your cart before trying again.';
      } finally {
        busy = false;
        render();
      }
    }
    button.addEventListener('click', add);
    render();
    return {
      add: add,
      syncMain: function (colour, size) {
        if (touched || busy) return;
        var sizeValues = controls.filter(function (control) { return /^size$/i.test(control.getAttribute('data-option-name')); }).map(function (control) { return control.getAttribute('data-value'); });
        var standard = sizeValues.find(function (value) { return /standard/i.test(value || ''); });
        var mappedSize = size;
        if (standard && /^(Double|Queen)$/i.test(size || '')) mappedSize = standard;
        if (standard && /^(King|Super King)$/i.test(size || '') && sizeValues.includes('King')) mappedSize = 'King';
        controls.forEach(function (control) {
          var name = control.getAttribute('data-option-name');
          var value = /colou?r/i.test(name) ? colour : /^size$/i.test(name) ? mappedSize : null;
          if (value && control.getAttribute('data-value') === value) selected[Number(control.getAttribute('data-cs-option'))] = value;
        });
        render();
      }
    };
  }

  function observeMainSelection(box, onSelection, Observer) {
    var previous = '';
    function sync() {
      var colourNode = box.querySelector('.colour-swatch.active');
      var sizeNode = box.querySelector('.size-btn.active');
      var colour = colourNode && colourNode.getAttribute('data-colour');
      var size = sizeNode && sizeNode.getAttribute('data-size');
      var signature = JSON.stringify([colour, size]);
      if (signature === previous) return;
      previous = signature;
      onSelection(colour, size);
    }
    var observer = new Observer(sync);
    observer.observe(box, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    sync();
    return observer;
  }

  var api = { exactVariant: exactVariant, money: money, addVariant: addVariant, createController: createController, observeMainSelection: observeMainSelection };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document || root.MyntFusionCrossSells) return;
  root.MyntFusionCrossSells = api;
  var controllers = [];
  function paymentPlacement() {
    var box = root.document.querySelector('.buy-box');
    var fallback = box && box.querySelector('.afterpay-line');
    if (!fallback) return;
    var host = box.querySelector('square-placement[data-page-type="product"]');
    if (!host) { fallback.hidden = false; return; }
    host.setAttribute('data-mf-pdp-payment', '');
    if (fallback.nextElementSibling !== host) fallback.insertAdjacentElement('afterend', host);
    var shadow = host.shadowRoot;
    var paragraph = shadow && shadow.querySelector('p.afterpay-paragraph');
    var ready = paragraph && /payment|instalment|installment/i.test(paragraph.textContent);
    fallback.hidden = !!ready;
    if (shadow && !host.dataset.csPaymentObserved) {
      host.dataset.csPaymentObserved = 'true';
      new MutationObserver(paymentPlacement).observe(shadow, { childList: true, subtree: true, characterData: true });
    }
  }
  function initPayment() {
    var box = root.document.querySelector('.buy-box');
    if (!box || box.dataset.csPaymentReady) return;
    box.dataset.csPaymentReady = 'true';
    paymentPlacement();
    new MutationObserver(paymentPlacement).observe(box, { childList: true, subtree: true });
    // The vendor may attach its shadow root without a light-DOM mutation.
    [250, 1000, 2500, 5000, 10000].forEach(function (delay) { root.setTimeout(paymentPlacement, delay); });
  }
  function init(scope) {
    initPayment();
    scope.querySelectorAll('.cross-sell-card[data-mf-cross-sell]').forEach(function (card) {
      if (card.dataset.csReady) return;
      card.dataset.csReady = 'true';
      var shopRoot = root.Shopify && root.Shopify.routes ? root.Shopify.routes.root : '/';
      var env = {
        root: shopRoot, path: root.location.pathname, fetch: root.fetch.bind(root),
        currency: card.getAttribute('data-currency'),
        drawer: root.document.querySelector('cart-notification') || root.document.querySelector('cart-drawer'),
        monsterRefresh: typeof root.monster_refresh === 'function' ? root.monster_refresh : null,
        onCart: function (cart) {
          root.document.querySelectorAll('[data-cart-count], .cart-count-bubble span, .cart-count, #CartCount').forEach(function (el) { el.textContent = cart.item_count; });
          root.document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        }
      };
      try {
        var controller = createController(card, env);
        controllers.push(controller);
        var colour = root.document.querySelector('.buy-box .colour-swatch.active');
        var size = root.document.querySelector('.buy-box .size-btn.active');
        controller.syncMain(colour && colour.getAttribute('data-colour'), size && size.getAttribute('data-size'));
      } catch (error) {
        card.querySelector('.cs-status').textContent = 'Please open the product to choose your options.';
      }
    });
    var box = root.document.querySelector('.buy-box');
    if (box && !box.dataset.csSelectionObserved) {
      box.dataset.csSelectionObserved = 'true';
      observeMainSelection(box, function (colour, size) {
        controllers.forEach(function (controller) { controller.syncMain(colour, size); });
      }, root.MutationObserver);
    }
  }
  root.document.addEventListener('mf-pdp:selection', function (event) {
    controllers.forEach(function (controller) { controller.syncMain(event.detail.colour, event.detail.size); });
  });
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', function () { init(root.document); });
  else init(root.document);
  root.document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})(typeof window !== 'undefined' ? window : globalThis);
